// Utility functions for time conversion and formatting

/**
 * Convert UTC time string to local time and format for display
 * @param {string} utcTimeString - Time in "HH:MM" or "HH:MM:SS" format (UTC)
 * @param {string} dateString - Date string in "YYYY-MM-DD" format
 * @returns {string} Formatted time in local timezone (e.g., "10:00 AM")
 */
export const convertUTCToLocalTime = (utcTimeString, dateString) => {
  if (!utcTimeString) return "Not specified";
  
  try {
    // Parse the UTC time
    const [hours, minutes] = utcTimeString.split(':').map(Number);
    
    // Create a UTC date object with the given date and time
    const utcDate = new Date(`${dateString}T${utcTimeString.padEnd(8, ':00')}.000Z`);
    
    // Get local time
    const localHours = utcDate.getHours();
    const localMinutes = utcDate.getMinutes();
    
    // Format to 12-hour format
    const ampm = localHours >= 12 ? 'PM' : 'AM';
    const displayHours = localHours % 12 || 12;
    const displayMinutes = String(localMinutes).padStart(2, '0');
    
    return `${displayHours}:${displayMinutes} ${ampm}`;
  } catch (error) {
    console.error('Error converting UTC to local time:', error);
    return utcTimeString;
  }
};

/**
 * Get the user's timezone for display purposes
 * @returns {string} User's timezone (e.g., "America/New_York")
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Convert local time to UTC (for sending to backend)
 * @param {Date} date - The date object
 * @param {string} timeString - Time in "HH:MM" format (local time)
 * @returns {string} UTC time in "HH:MM" format
 */
export const convertLocalTimeToUTC = (date, timeString) => {
  try {
    const [hours, minutes] = timeString.split(':').map(Number);
    const localDateTime = new Date(date);
    localDateTime.setHours(hours, minutes, 0, 0);
    
    const utcHours = localDateTime.getUTCHours();
    const utcMinutes = localDateTime.getUTCMinutes();
    
    return `${String(utcHours).padStart(2, '0')}:${String(utcMinutes).padStart(2, '0')}`;
  } catch (error) {
    console.error('Error converting local time to UTC:', error);
    return timeString;
  }
};

/**
 * Format date for display
 * @param {string} dateString - Date string in "YYYY-MM-DD" format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date
 */
export const formatDateForDisplay = (dateString, options = {}) => {
  if (!dateString) return "Not specified";
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      ...options
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}; 